import React, { useCallback, useState } from 'react';
import { HudIcon } from '../Icons/HudIcons';
import {
  Combobox,
  useCombobox,
  Popover,
  FocusTrap,
  InputBase,
} from '@mantine/core';
import {
  ComboboxItem,
  ComboboxItemGroup,
} from '@mantine/core/lib/components/Combobox/Combobox.types';
import {
  GlobalStyle,
  Container,
  StyledPopover,
  ClearButton,
  AutoCompleteContainer,
  ServiceGroupContainer,
  popoverStyle,
} from './FlowFilter.styles';
import { getIconForFlow, FlowType } from '../utils';
import { SkeletonFilled } from '../Skeleton/Skeleton';

export interface flowComboboxItem extends ComboboxItem {
  flowType: FlowType;
}

export interface FlowFilterProps {
  data: ComboboxItemGroup<flowComboboxItem>[];
  selectedFlow: string;
  onSelectFlow: (value: string) => void;
  onClear: () => void;
  loading?: boolean;
}

function findServiceNameByFlowId(
  data: ComboboxItemGroup<flowComboboxItem>[],
  flowId: string,
): string {
  const groupItem = data.find((groupItem) => {
    return groupItem.items.some((item: ComboboxItem) => item.value === flowId);
  });

  return groupItem ? groupItem.group : '';
}

interface ServiceGroupProps {
  label: string;
}

const ServiceGroup: React.FC<ServiceGroupProps> = ({ label }) => {
  return (
    <ServiceGroupContainer>
      <HudIcon size="20" icon={'entities.servicesBlank'} /> {label}
    </ServiceGroupContainer>
  );
};

export const FlowFilter: React.FC<FlowFilterProps> = ({
  data,
  selectedFlow, // this represents both the service and the flow
  onSelectFlow,
  onClear,
  loading,
}) => {
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const allItems = data.reduce<flowComboboxItem[]>(
    (acc, group) => [...acc, ...group.items],
    [],
  );

  const [search, setSearch] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);

  // right now the service shows just what was selected,
  // since we don't have filtering by service yet
  const service = findServiceNameByFlowId(data, selectedFlow);
  const selectedFlowItem = allItems.find((item) => item.value === selectedFlow);
  const flowPath = selectedFlowItem?.label;

  const _onClear = () => {
    setSearch('');
    setIsSearchOpen(false);
    onClear();
  };

  // keep track of this state to allow toggling the search from the
  // "showing data from all flows" button while search is open
  // This solves the issue where clicking outside the flow selector closes it,
  // followed by being opened from hitting the button
  const onFlowSelectClose = useCallback(() => {
    setIsSearchClosing(true);
    setTimeout(() => {
      setIsSearchClosing(false);
    }, 100);
  }, []);

  const toggleSearch = () => {
    if (!isSearchClosing && !loading) {
      setIsSearchOpen(!isSearchOpen);
      setSearch('');
    }
  };

  const shouldFilterOptions = allItems.every((item) => item.label !== search);
  const filteredGroups = data.map((group) => {
    const filteredOptions = shouldFilterOptions
      ? group.items.filter((item) =>
          item.label.toLowerCase().includes(search.toLowerCase().trim()),
        )
      : group.items;

    return { ...group, options: filteredOptions };
  });

  const totalOptions = filteredGroups.reduce(
    (acc, group) => acc + group.options.length,
    0,
  );

  const groups = filteredGroups.map((group) => {
    const options = group.options.map((item) => (
      <Combobox.Option value={item.value} key={item.value}>
        {item && <HudIcon size="20" icon={getIconForFlow(item.flowType)} />}{' '}
        {item.label}
      </Combobox.Option>
    ));

    return (
      <Combobox.Group
        label={<ServiceGroup label={group.group} />}
        key={group.group}
      >
        {options}
      </Combobox.Group>
    );
  });

  const isFlowSelected = !(selectedFlow === '' || selectedFlow === undefined);

  const flowSelectedElement = (
    <div className="selectedFlowContainer">
      <div className="selectedFlowProperty">
        <HudIcon size="20" icon={'entities.servicesBlank'} /> {service}
      </div>
      <div className="selectedFlowProperty">
        {selectedFlowItem && (
          <HudIcon size="20" icon={getIconForFlow(selectedFlowItem.flowType)} />
        )}{' '}
        {flowPath}
      </div>
    </div>
  );

  const flowUnselectedElement = (
    <span className="unselectedFlowContainer">Showing data from all flows</span>
  );

  const flowSkeleton = <SkeletonFilled height={20} />;

  return (
    <Container className={isFlowSelected ? 'flowSelected' : ''}>
      <GlobalStyle />
      <div className="label">Filter by Flow</div>
      <StyledPopover
        position="bottom"
        opened={isSearchOpen}
        width="target"
        offset={8}
        onClose={onFlowSelectClose}
      >
        <Popover.Target>
          {loading ? (
            <button className="openFilter">
              {flowSkeleton}
            </button>
          ) : (
            <button className="openFilter" onClick={toggleSearch}>
              {isFlowSelected ? flowSelectedElement : flowUnselectedElement}
            </button>
          )}
        </Popover.Target>
        {isFlowSelected && (
          <ClearButton onClick={_onClear}>
            <HudIcon icon="other.close" size="20px" />
          </ClearButton>
        )}
        <Popover.Dropdown style={popoverStyle}>
          <FocusTrap active={isSearchOpen}>
            <AutoCompleteContainer>
              <Combobox
                store={combobox}
                withinPortal={false}
                onOptionSubmit={(val) => {
                  setSearch(val);
                  combobox.closeDropdown();
                  onSelectFlow(val);
                  setIsSearchOpen(false);
                }}
              >
                <Combobox.Target>
                  <InputBase
                    value={search}
                    onChange={(event) => {
                      combobox.openDropdown();
                      combobox.updateSelectedOptionIndex();
                      setSearch(event.currentTarget.value);
                    }}
                    onClick={() => combobox.openDropdown()}
                    onFocus={() => combobox.openDropdown()}
                    onBlur={() => {
                      combobox.closeDropdown();
                      setSearch('');
                      setIsSearchOpen(false);
                    }}
                    placeholder="Search..."
                    leftSection={<HudIcon size="20" icon={'other.search'} />}
                    data-autofocus
                  />
                </Combobox.Target>
                <Combobox.Options>
                  {totalOptions > 0 ? (
                    groups
                  ) : (
                    <Combobox.Empty>Nothing found</Combobox.Empty>
                  )}
                </Combobox.Options>
              </Combobox>
            </AutoCompleteContainer>
          </FocusTrap>
        </Popover.Dropdown>
      </StyledPopover>
    </Container>
  );
};
